/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { JSX } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  $createLinkNode,
  $isAutoLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import clsx from 'clsx';
import {
  $getSelection,
  $isLineBreakNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  getDOMSelection,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ControlledSystemCheckbox } from '@cms/components/data/Checkbox';
import { INPUT_VARIANTS } from '@cms/utils/constants';
import { Button } from '../../../data/Button';
import { ControlledSystemTextInput } from '../../../data/TextInput';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { setFloatingElemPositionForLinkEditor } from '../../utils/setFloatingElemPositionForLinkEditor';
import { sanitizeUrl } from '../../utils/url';

import { MdClose } from 'react-icons/md';

function preventDefault(
  event: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLElement>,
): void {
  event.preventDefault();
}

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null,
  );

  const methods = useForm({
    defaultValues: {
      linkText: ' ',
      linkUrl: '',
      openInNewTab: true,
    },
    mode: 'all',
    resolver: yupResolver(yup.object({
      linkText: yup.string().required('Link text is required'),
      linkUrl: yup.string().required('Link URL is required').matches(
        /^((https?:\/\/)?)[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/,
        'Invalid URL'
      ),
      openInNewTab: yup.boolean().required('Open in new tab is required'),
    })),
  })

  const { formState, watch, setValue, reset } = methods;

  const [linkText, linkUrl, openInNewTab] = watch(['linkText', 'linkUrl', 'openInNewTab']);

  const $updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      if (linkParent) {
        setValue('linkUrl', linkParent.getURL());
        setValue('openInNewTab', linkParent.getTarget() === '_blank');
      } else if ($isLinkNode(node)) {
        setValue('linkUrl', node.getURL());
        setValue('openInNewTab', node.getTarget() === '_blank');
      } else {
        setValue('linkUrl', '');
        setValue('openInNewTab', true);
      }

      setValue('linkText', node.getTextContent());
    }
    else if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes();
      if (nodes.length > 0) {
        const node = nodes[0];
        const parent = node.getParent();
        if ($isLinkNode(parent)) {
          setValue('linkUrl', parent.getURL());
          setValue('openInNewTab', parent.getTarget() === '_blank');
        } else if ($isLinkNode(node)) {
          setValue('linkUrl', node.getURL());
          setValue('openInNewTab', node.getTarget() === '_blank');
        } else {
          setValue('linkUrl', '');
          setValue('openInNewTab', true);
        }
      }
    }

    const editorElem = editorRef.current;
    const nativeSelection = getDOMSelection(editor._window);
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (selection !== null && rootElement !== null && editor.isEditable()) {
      let domRect: DOMRect | undefined;

      if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        if (nodes.length > 0) {
          const element = editor.getElementByKey(nodes[0].getKey());
          if (element) {
            domRect = element.getBoundingClientRect();
          }
        }
      } else if (
        nativeSelection !== null &&
        rootElement.contains(nativeSelection.anchorNode)
      ) {
        domRect =
          nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      }

      if (domRect) {
        domRect.y += 40;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }
      setLastSelection(null);
      setIsLinkEditMode(false);
      setValue('linkUrl', 'https://');
    }

    return true;
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem.parentElement, editor, $updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor, $updateLinkEditor, setIsLink, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateLinkEditor();
    });
  }, [editor, $updateLinkEditor]);

  // useEffect(() => {
  //   if (isLinkEditMode && inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, [isLinkEditMode, isLink]);

  const monitorInputInteraction = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === 'Enter') {
      handleLinkSubmission(event);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleLinkSubmission = (
    event:
      | React.KeyboardEvent<HTMLDivElement>
      | React.MouseEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    if (lastSelection !== null) {
      editor.update(() => {

        editor.dispatchCommand(
          TOGGLE_LINK_COMMAND,
          { url: sanitizeUrl(linkUrl), target: openInNewTab ? '_blank' : '_self' },
        );

        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          const parent = getSelectedNode(selection).getParent();
          if ($isLinkNode(parent)) {
            const linkNode = $createLinkNode(parent.getURL(), {
              rel: parent.__rel,
              target: parent.__target,
              title: parent.__title,
            });
            const child = parent?.getFirstChild();
            if ($isTextNode(child)) {
              child.setTextContent(linkText);
            }

            parent.replace(linkNode, true);
          }

          $setSelection(null)
        }
      });

      reset();
      setIsLinkEditMode(false);
      handleCloseWindow();
    }
  };

  const handleCloseWindow = () => {
    setIsLink(false);
  }

  return (
    <div
      ref={editorRef}
      className={clsx(
        "flex absolute top-0 left-0 z-10 max-w-[400px] w-full",
        "opacity-0 bg-white shadow-xl rounded-lg",
        "transition-all duration-300"
      )}
    >
      {!isLink ? null : (
        <FormProvider {...methods}>
          <div className='w-full flex flex-col gap-2'>
            <div
              className={clsx(
                'px-4 py-2 flex items-center justify-between',
                INPUT_VARIANTS['white_text_dark_bg']
              )}
            >
              <p>EMBED LINK</p>
              <MdClose
                size={24}
                className='cursor-pointer'
                onClick={handleCloseWindow}
              />
            </div>

            <div
              className='p-4 flex flex-col gap-2'
            >
              <ControlledSystemTextInput
                name='linkText'
                className="link-input"
                description='Text'
                inputSize='full'
                placeholder='Your text here'
                onKeyDown={(event) => {
                  monitorInputInteraction(event);
                }}
              />
              <ControlledSystemTextInput
                name='linkUrl'
                className="link-input"
                description='Link'
                inputSize='full'
                placeholder='https://www.example.com'
                onKeyDown={(event) => {
                  monitorInputInteraction(event);
                }}
              />

              <div className='self-end justify-self-end place-self-end'>
                <ControlledSystemCheckbox
                  name='openInNewTab'
                  label='Open in new tab'
                />
              </div>
            </div>

            <div className='px-4 pt-2 pb-4 flex items-center gap-2 self-end'>
              <Button
                type='button'
                inputVariant='gray_text_white_bg'
                onMouseDown={preventDefault}
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  event.preventDefault();
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                }}
              >
                Unlink
              </Button>
              <Button
                type='button'
                inputVariant='white_text_blue_bg'
                onMouseDown={preventDefault}
                onClick={handleLinkSubmission}
                disabled={!formState.isValid}
              >
                Apply
              </Button>
            </div>
          </div>
        </FormProvider>
      )}
    </div>
  )
}

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLinkEditMode: boolean,
  setIsLinkEditMode: Dispatch<boolean>,
): JSX.Element | null {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function $updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection);
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode);
        const focusAutoLinkNode = $findMatchingParent(
          focusNode,
          $isAutoLinkNode,
        );
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setIsLink(false);
          return;
        }
        const badNode = selection
          .getNodes()
          .filter((node) => !$isLineBreakNode(node))
          .find((node) => {
            const linkNode = $findMatchingParent(node, $isLinkNode);
            const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode);
            return (
              (focusLinkNode && !focusLinkNode.is(linkNode)) ||
              (linkNode && !linkNode.is(focusLinkNode)) ||
              (focusAutoLinkNode && !focusAutoLinkNode.is(autoLinkNode)) ||
              (autoLinkNode &&
                (!autoLinkNode.is(focusAutoLinkNode) ||
                  autoLinkNode.getIsUnlinked()))
            );
          });
        if (!badNode) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      } else if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        if (nodes.length === 0) {
          setIsLink(false);
          return;
        }
        const node = nodes[0];
        const parent = node.getParent();
        if ($isLinkNode(parent) || $isLinkNode(node)) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank');
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem,
  );
}

export function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  anchorElem?: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(
    editor,
    anchorElem,
    isLinkEditMode,
    setIsLinkEditMode,
  );
}
